import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { resolveLandingRoute, safeInternalPath } from "../lib/experienceResolver";
import { LOGIN } from "../constants/testIds";
import { toast } from "sonner";
import BtcLogo from "../components/BtcLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resolverFailed, setResolverFailed] = useState(false);
  const from = safeInternalPath(location.state?.from?.pathname);

  const attemptResolve = async () => {
    try {
      const dest = from || (await resolveLandingRoute());
      setResolverFailed(false);
      navigate(dest, { replace: true });
    } catch (_e) {
      // Phase 2 verification §4 — no silent workspace fallback.
      // Keep the user on this screen; expose a visible retry.
      setResolverFailed(true);
      setErr("We could not determine where to send you. Please retry.");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setResolverFailed(false);
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      await attemptResolve();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      <main id="main-content" className="flex-1 bg-white px-6 py-12 lg:px-16 flex flex-col">
        <Link to="/" className="inline-flex items-center gap-2">
          <BtcLogo className="h-12 w-auto" />
        </Link>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            <p className="btc-eyebrow mb-3">SIGN IN</p>
            <h1 className="text-4xl font-extrabold tracking-tight text-[#0B132B] mb-3">
              Welcome back.
            </h1>
            <p className="text-[#4B5563] mb-8">
              Sign in to your BTC portal. New here?{" "}
              <Link to="/register" data-testid={LOGIN.registerLink} className="text-[#1A5FAB] font-semibold">
                Create an account
              </Link>
              .
            </p>
            <form onSubmit={onSubmit} className="space-y-4" aria-label="Sign in to BTC">
              <div>
                <label htmlFor="login-email" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2 block">Email</label>
                <input
                  id="login-email"
                  data-testid={LOGIN.emailInput}
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 transition"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="login-password" className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">Password</label>
                  <Link to="/forgot-password" data-testid={LOGIN.forgotPasswordLink} className="text-xs font-semibold text-[#1A5FAB]">
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="login-password"
                  data-testid={LOGIN.passwordInput}
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3.5 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/30 transition"
                  placeholder="••••••••"
                />
              </div>
              {err && <div data-testid="login-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{err}</div>}
              {resolverFailed ? (
                <button
                  type="button"
                  data-testid="login-resolver-retry"
                  onClick={attemptResolve}
                  className="w-full btc-btn-primary justify-center"
                >
                  Retry
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  data-testid={LOGIN.submitButton}
                  disabled={busy}
                  className="w-full btc-btn-primary justify-center disabled:opacity-60"
                >
                  {busy ? "Signing in..." : "Sign in"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </form>
          </div>
        </div>
      </main>
      <div
        className="hidden lg:flex flex-1 relative items-end p-12 bg-cover bg-center"
        style={{
          backgroundImage: "url('/img/hero.jpg')",
        }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/90 via-[#0B132B]/40 to-transparent" />
        <div className="relative text-white max-w-md">
          <Heart className="w-10 h-10 text-[#23803F] mb-4" fill="#2D9B4E" />
          <h2 className="text-3xl font-extrabold tracking-tight mb-3 leading-tight">
            No one faces cancer alone.
          </h2>
          <p className="text-white/80 text-lg">
            Your community, your care team, and your resources—all in one place.
          </p>
        </div>
      </div>
    </div>
  );
}
